import {
  chainCommands,
  exitCode,
  createParagraphNear,
  liftEmptyBlock,
  splitBlock,
  splitBlockAs,
  toggleMark,
  selectParentNode,
  deleteSelection,
  joinBackward,
  selectNodeBackward,
  lift,
  wrapIn,
  setBlockType
} from "prosemirror-commands";
import { liftListItem, sinkListItem, splitListItem, wrapInList } from "prosemirror-schema-list";
import { customSchema } from "../schemas/custom-schema";
import { findWrapping } from "prosemirror-transform";
import { TextSelection, NodeSelection, Selection } from "prosemirror-state";
import { Fragment } from "prosemirror-model";
import { uniqueIdsKey } from "../plugins/transactional/unique-ids";
import { menuPluginKey } from "../plugins/menu/menu-plugin";

const {
  paragraph,
  orderedList,
  unorderedList,
  figureGraphic,
  figure,
  image,
  figcaption,
  embed,
  table,
  table_row,
  table_cell,
  details,
  summary,
  detailsContent,
  paragraphSummary
} = customSchema.nodes;
const {
  bold,
  italics,
  underline,
  strikeThrough,
  smallCaps,
  subscript,
  superscript,
  fontColor,
  backgroundColor,
  fontSize,
  inlineCode,
  customLink
} = customSchema.marks;

export const insertHardBreak = chainCommands(
  exitCode,
  (state, dispatch) => {
    dispatch(state.tr.replaceSelectionWith(customSchema.nodes.hardBreak.create()).scrollIntoView());
  }
);

export function createNewBlockAfterFigure(state, dispatch) {
  const { $head } = state.selection,
    { tr } = state;

  if($head.parent.type.name === "figcaption") {
    if($head.parent.textContent.length === 0 || $head.pos === $head.end()) {
      const figurePos = $head.before($head.depth - 1),
        figureNode = $head.node($head.depth - 1);

      if(figureNode && figureNode.type.name === "figure") {
        const newParagraph = paragraph.create();
        tr.insert(figurePos + figureNode.nodeSize, newParagraph);
        tr.setSelection(Selection.near(tr.doc.resolve(figurePos + figureNode.nodeSize + 1)));
        if(dispatch) {
          dispatch(tr);
        }

        return true;
      }
    }
  }

  return false;
};

function splitListItemWithoutAttrs(itemType) {
  return (state, dispatch) => {
    const { $from, $to } = state.selection;

    if($from.depth < 2 || !$from.sameParent($to)) {
      return false;
    }
    
    const grandParent = $from.node(-1),
      emptyChild = grandParent.content.content[0].content.size === 0;

    if(grandParent.type.name !== itemType.name || emptyChild) {
      return false;
    }

    if (dispatch) {
      let tr = state.tr.delete($from.pos, $to.pos),
        nextType = $to.pos === $from.end() ? grandParent.contentMatchAt(0).defaultType : null;
      
      const newAttrs = { id: null }; 

      tr.split(tr.mapping.map($from.pos), 2, [
        { type: itemType, attrs: newAttrs }
      ]);
      
      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}

export const enterCommand = chainCommands(
  splitListItemWithoutAttrs(customSchema.nodes.listItem),
  //splitListItem(customSchema.nodes.listItem),
  newlineInCode,
  createNewBlockAfterFigure,
  createParagraphNear,
  liftEmptyBlock,
  splitBlock
);

export const backspaceCommand = chainCommands(
  deleteFigure,
  customBackspaceCommand,
  deleteSelection,
  joinBackward,
  selectNodeBackward
);

// Probablemente se van a eliminar.
export const toggleBoldCommand = toggleMark(customSchema.marks.bold);
export const toggleItalicsCommand = toggleMark(customSchema.marks.italics);
export const toggleUnderlineCommand = toggleMark(customSchema.marks.underline);

export function toggleMarkWithAttrs(markType, attrs = {}) {
  return (state, dispatch) => {
    const { from, to, empty, $from } = state.selection,
      { tr } = state,
      notAllowed = isNodesSetActive(state, [customSchema.nodes.ghost]) || state.doc.type.name === "ghost";

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede añadir la marca indicada en la selección actual. Verifique e intente de nuevo.");
      }

      return false;
    }

    if(empty) {
      const storedMarks = state.storedMarks || $from.marks(),
        existingMark = storedMarks.find(
          mark => mark.type.name === markType.name && 
          Object.keys(attrs).every(key => mark.attrs[key] === attrs[key])
        );

      if(existingMark) {
        if(dispatch) {
          dispatch(tr.removeStoredMark(markType));
        }

        return true;
      } else {
        const newMark = markType.create(attrs);

        if(!newMark) {
          return false;
        }

        if(dispatch) {
          dispatch(tr.addStoredMark(newMark));
        }

        return true;
      }
    } else {
      let markRemoved = false;
      state.doc.nodesBetween(from, to, (node) => {
        if(
          node.marks.some(
            mark => mark.type.name === markType.name && 
            Object.keys(attrs).every(key => mark.attrs[key] === attrs[key])
            )
        ) {
          markRemoved = true;

          return false;
        }
      });

      if (markRemoved) {
        tr.removeMark(from, to, markType);
      } else {
        const newMark = markType.create(attrs);

        if(!newMark) {
          return false;
        }

        tr.addMark(from, to, newMark);
      }
      
      if(dispatch) {
        dispatch(tr);
      }

      return true;
    }
  };
};

export function isCustomNodeAllowed(view, nodeType, attrs = null) {
  const { from, to } = view.state.selection,
    { tr } = view.state,
    newNode = attrs === null ? nodeType.create() : nodeType.create(attrs),
    canReplace = tr.replaceSelectionWith(newNode),
    canWrap = wrapIn(nodeType, attrs)(view.state, null);

  return canWrap || canReplace.docChanged;
}

export function isCustomMarkAllowed(state, markType) {
  const { from, to, empty, $cursor, $from } = state.selection;

  if(empty) {
    const storedMarks = state.storedMarks || $from.marks();
    if(!!storedMarks && markType.isInSet(storedMarks)) {
      return true;
    }

    if($cursor && $cursor.parent.type.allowsMarkType(markType)) {
      if(
        //markType === customSchema.marks.link || // No link in cursor.
        //markType === customSchema.marks.customLink || // Nor notes link.
        (
          customSchema.marks.link.isInSet(storedMarks) &&
          (
            markType.name === "customLink" ||
            markType.name === "fontColor" ||
            markType.name === "backgroundColor" ||
            markType.name === "fontSize"
          )
        ) ||
        (
          customSchema.marks.customLink.isInSet(storedMarks) &&
          (
            markType.name === "link" ||
            markType.name === "fontColor" ||
            markType.name === "backgroundColor" ||
            markType.name === "fontSize"
          )
        )
      ) {
        return false;
      }
      
      return true;
    }

    return false;
  }

  let allowed = false;

  if(
    (state.doc.rangeHasMark(from, to, customSchema.marks.link)
      && (
        markType.name === "customLink" ||
        markType.name === "fontColor" ||
        markType.name === "backgroundColor" ||
        markType.name === "fontSize"
      )
    ) ||
    (state.doc.rangeHasMark(from, to, customSchema.marks.customLink) &&
      (
        markType.name === "link" ||
        markType.name === "fontColor" ||
        markType.name === "backgroundColor" ||
        markType.name === "fontSize"
      )
    )
  ) {
    return false;
  }

  state.doc.nodesBetween(from, to, (node) => {
    if(node.isTextblock && node.type.allowsMarkType(markType)) {
      allowed = true;

      return false;
    }
  });

  return allowed;
}

export function isCustomMarkActive(state, markType, attrs = {}) {
  const { from, to, empty, $from } = state.selection;

  if (empty) {
    // Check stored marks for empty selection
    const storedMarks = state.storedMarks || $from.marks();
    return storedMarks.some(mark => mark.type.name === markType.name && 
          Object.keys(attrs).every(key => mark.attrs[key] === attrs[key]));
  } else {
    // Check range for non-empty selection
    let found = false;
    state.doc.nodesBetween(from, to, (node) => {
      if (found) return false;
      found = node.marks.some(mark => mark.type.name === markType.name && 
          Object.keys(attrs).every(key => mark.attrs[key] === attrs[key]));
      return !found;
    });
    return found;
  }
}

export function isNodeActive(state, nodeType, attrs = {}) {
  const { from, to, empty, $from } = state.selection;

  if(empty) {
    for(let i = $from.depth; i >= 0; i--) {
      const node = $from.node(i);
      if(!!node && node.type.name === nodeType?.name && Object.keys(attrs).every(key => node.attrs[key] === attrs[key])) {
        return true;
      }
    }

    return false;
  } else {
    let isActive = false;
    state.doc.nodesBetween(from, to, (node) => {
      if(node.type.name === nodeType?.name && Object.keys(attrs).every(key => node.attrs[key] === attrs[key])) {
        isActive = true;

        return false;
      }
    });

    return isActive;
  }
}

export function removeCustomMarkCommand(markType) {
  return function(state, dispatch) {
    const { from, to, empty, $from } = state.selection,
      { tr } = state;
    let somethingHappened = false;

    //if (dispatch) {
      if (empty) {
        const storedMarks = state.storedMarks || $from.marks(),
          existingMark = storedMarks.some(mark => mark.type.name === markType.name);
        // Handle empty selection (stored marks)
        if (existingMark) {
          tr.removeStoredMark(markType);
          somethingHappened = true;
        }
      } else {
        // Handle non-empty selection (document range)
        tr.removeMark(from, to, markType);
        somethingHappened = true;
      }
      
      if (somethingHappened) {
        if(dispatch) {
          dispatch(tr);
        }

        return true;
      }
    //}
    return false;
  };
}

function extendRangeToWordsBoundaries(state) {
  const { selection, doc } = state,
    { from, to } = selection;
  let $from = doc.resolve(from),
    $to = doc.resolve(to),
    start = from,
    lineStart = $from.start();

  while(start > lineStart) {
    if(/\w/.test(doc.textBetween(start - 1, start))) {
      start--;
    } else {
      break;
    }
  }

  let end = to,
    lineEnd = $to.end();

  while(end < lineEnd) {
    if(/\w/.test(doc.textBetween(end, end + 1))) {
      end++;
    } else {
      break;
    }
  }

  return { from: start, to: end };
}

export function addCustomMarkCommand(markType, attrs = {}, excludingMarks = [], excludingNodes = [], allowedPredicate = true) {
  return function(state, dispatch) {
    const { from, to, empty, $from } = state.selection;
    const { tr } = state;
    const newMark = markType.create(attrs),
    isAllowed = allowedPredicate && !isNodesSetActive(state, excludingNodes);
    let somethingHappened = false;
    
    if(!isAllowed) {
      if(dispatch) {
        alert("No se puede añadir la marca solicitada en la selección actual. Verifique e intente nuevamente.");
      }
      
      return false;
    }

    for(const mark of excludingMarks) {
      if(isCustomMarkActive(state, mark)) {
        return false;
      }
    }

    if (empty) {
      const storedMarks = state.storedMarks || $from.marks(),
        existingMark = storedMarks.some(mark => mark.type.name === markType.name);
      // Handle empty selection (stored marks)
      if (existingMark) {
        tr.removeStoredMark(markType);
      }
      tr.addStoredMark(newMark);
      somethingHappened = true;
    } else {
      // Handle non-empty selection (document range)
      tr.removeMark(from, to, markType);
      tr.addMark(from, to, newMark);
      somethingHappened = true;
    }
    
    if (somethingHappened) {
      if(dispatch) {
        dispatch(tr);
      }

      return true;
    }

    return false;
  };
}

export function removeFullMarkCommand(markType) {
  return (state, dispatch) => {
    const { selection, tr } = state,
      { $from, empty, ranges } = selection;

    const range = getFullMarkRange(state, markType);
    tr.removeMark(range.from, range.to, markType);
    if(tr.docChanged) {
      if(dispatch) {
        dispatch(tr.scrollIntoView());
      }
      
      return true;
    }

    return false;
  }
}

export function addFullMarkCommand(markType, attrs = {}, bindWords = false, excludingMarks = [], excludingNodes = []) {
  return (state, dispatch) => {
    const { selection, tr, doc } = state,
      { $from, $to, empty, ranges, from, to } = selection,
      newMark = markType.create(attrs),
      notAllowed = ["note", "ghost"].includes(doc.type.name) || isNodesSetActive(state, excludingNodes);
      
    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar una liga en la selección actual. Verifique e intene nuevamente.");
      }

      return false;
    }

    for(const mark of excludingMarks) {
      if(isCustomMarkActive(state, mark)) {
        if(dispatch) {
          alert("No se puede insertar una liga en la selección actual. Verifique e intene nuevamente.");
        }

        return false;
      }
    }

    const range = bindWords ? extendRangeToWordsBoundaries(state) : getFullMarkRange(state, markType);
    tr.removeMark(range.from, range.to, markType);
    tr.addMark(range.from, range.to, newMark);

    if(tr.docChanged) {
      if(dispatch) {
        dispatch(tr.scrollIntoView());
      }
      
      return true;
    }

    return false;
  }
}

export function getMarkAttrs(state, markType) {
  const { from, to, empty, $from } = state.selection;

  if (empty) {
    const storedMarks = state.storedMarks || $from.marks(),
      foundMark = storedMarks.find(mark => mark.type.name === markType.name);

    if(foundMark == null) {
      return null;
    }

    return foundMark.attrs;
  } else {
    let found = false;
    state.doc.nodesBetween(from, to, (node) => {
      if (found) return false;
      found = node.marks.find(mark => mark.type.name === markType.name);
      return !found;
    });
    return !found ? null : found.attrs;
  }
}

export function getNodeAttrs(state, nodeType) {
  const { from, to, empty, $from, $to } = state.selection,
    { doc } = state;

  if (empty) {
    for(let i = $from.depth; i >= 0; i--) {
      const node = $from.node(i);

      if(node.type.name === nodeType.name) {
        return node.attrs;
      }
    }

    return null;
  } else {
    let foundAttrs = null;
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if(node.type.name === nodeType.name) {
        foundAttrs = node.attrs;

        return false;
      }
    });

    return foundAttrs;
  }
}

export function getNode(state, nodeType) {
  const { from, to, empty, $from, $to } = state.selection,
    { doc } = state;

  if (empty) {
    for(let i = $from.depth; i >= 0; i--) {
      const node = $from.node(i);

      if(node.type.name === nodeType.name) {
        return node;
      }
    }

    return null;
  } else {
    let found = null;
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if(node.type.name === nodeType.name) {
        found = node;

        return false;
      }
    });

    return found;
  }
}

export function getFullMarkRange(state, markType, selectionOnly = false) {
  const { selection, doc } = state,
    { $from, $to, empty, from, to } = selection,
    storedMarks = state.storedMarks || $from.marks();

  if(empty) {
    const foundMark = storedMarks.find(mark => mark.type.name === markType.name);
    if(!!foundMark) {
      const range = getMarkRange(doc, $from.pos, $from.pos, markType);

      return { from: range.from, to: range.to, foundMark };
    }

    return { from, to, mark: markType };
  } else {
    const hasMark = doc.rangeHasMark($from.pos, $to.pos, markType);
    if(hasMark && selectionOnly) {
      return { from: $from.pos, to: $to.pos, mark: markType };
    }

    let currentMark = null,
      fullRange = { from: -1, to: -1 };
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if(node.marks.some(mark => mark.type.name === markType.name)) {
        if(currentMark === null) {
          currentMark = markType.isInSet(node.marks);
          fullRange.from = pos;
        }
        fullRange.to = pos + node.nodeSize;
      }
    });
    if(currentMark) {
      return { from: fullRange.from, to: fullRange.to, mark: markType };
    }

    return { from, to, mark: markType };
  }
}

export function getFullMarkRangeFromPos(node, pos) {
  return { from: pos, to: pos + node.nodeSize };
}

// Probablemente se elimine.
export function findDeepestAncestor(selection, nodeType) {
  const { $from } = selection;
  for(let i = $from.depth; i > 0; i--) {
    const node = $from.node(i);
    if(node.type.name === nodeType.name) {
      return { node, pos: $from.before(i) };
    }

    return null;
  }
};

export function wrapOrModifyListNode(listType, attrs = {}) {
  return (state, dispatch) => {
    const { selection, tr, doc } = state,
      { $from, $to } = selection,
      notAllowed = isNodesSetActive(state, [customSchema.nodes.note, customSchema.nodes.figcaption]) || ["note", "ghost"].includes(doc.type.name);
    let deepestListPos = -1,
      deepestListNode = null,
      node;
      
    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar una lista en la selección actual. Verifique e intente nuevamente.");
      }
      
      return false;
    }

    for(let i = $from.depth; i > 0; i--) {
      node = $from.node(i);
      if(node.type.name === "orderedList" || node.type.name === "unorderedList") {
        deepestListNode = node;
        deepestListPos = $from.before(i);
        break;
      }
    }

    if(deepestListNode) {
      if(dispatch) {
        tr.setNodeMarkup(deepestListPos, listType, attrs);
        dispatch(tr.scrollIntoView());
      }

      return true;
    }

    return wrapInList(listType, attrs)(state, dispatch);
  }
}

export function isNodesSetActive(state, nodes, attrs = {}) {
  const { from, to, empty, $from } = state.selection;

  if(empty) {
    for(let i = $from.depth; i >= 0; i--) {
      const node = $from.node(i);
      if(!!node && nodes.includes(node.type) && Object.keys(attrs).every(key => node.attrs[key] === attrs[key])) {
        return true;
      }
    }

    return false;
  } else {
    let isActive = false;
    state.doc.nodesBetween(from, to, (node) => {
      if(nodes.includes(node.type) && Object.keys(attrs).every(key => node.attrs[key] === attrs[key])) {
        isActive = true;

        return false;
      }
    });

    return isActive;
  }
}

export function customSinkListItem(nodeType) {
  return (state, dispatch, view) => {
    const notAllowed = !isNodeActive(state, nodeType) ||
      isNodeActive(state, customSchema.nodes.notesList);

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede anidar el elemento de la lista en la selección. Verifique e intente de nuevo.");
      }

      return false;
    }

    return sinkListItem(nodeType)(state, dispatch);
  }
}

export function customLiftListItem(nodeType) {
  return (state, dispatch, view) => {
    const notAllowed = !isNodeActive(state, nodeType) ||
      isNodeActive(state, customSchema.nodes.notesList);

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede anidar el elemento de la lista en la selección. Verifique e intente de nuevo.");
      }

      return false;
    }

    return liftListItem(nodeType)(state, dispatch);
  }
}

export function insertCharacter(char) {
  return (state, dispatch, view) => {
    if(!dispatch) {
      return true;
    }

    const { from, to } = state.selection;
    let { tr } = state;
    tr.insertText(char, from, to);
    dispatch(tr);

    return true;
  }
}

export function insertParagraph(attrs = {}) {
  return (state, dispatch, view) => {
    const notAllowed = isNodesSetActive(
      state,
      [customSchema.nodes.figcaption, customSchema.nodes.horizontalRule, customSchema.nodes.embed, customSchema.nodes.pdfContainer, customSchema.nodes.note/*, customSchema.nodes.ghost*/]
    ) || ["note"/*, "ghost"*/].includes(state.doc.type.name);

    if(notAllowed) {
      if(dispatch) {
        alert("No es posible insertar un nodo de párrafo en la selección actual. Verifique e intente de nuevo.");
      }

      return false;
    }

    if(!!attrs.id) {
      const { ids } = uniqueIdsKey.getState(state),
        { id } = attrs;
      if(!/^[a-zA-Z][a-zA-Z0-9\-_:.]*$/.test(id)) {
        alert("Identificador '" + id + "' inválido. No se asignó al elemento actual para mantener consistencia.");
        attrs.id = null
      } else if(ids.has(id)) {
        alert("Identificador '" + id + "' utilizado anteriormente. No se asignó al elemento actual para mantener consistencia.");
        attrs.id = null
      }
    }

    setBlockType(customSchema.nodes.paragraph, attrs)(state, dispatch);

    return true;
  }
}

export function insertHeading(attrs = {}) {
  return (state, dispatch, view) => {
    const notAllowed = isNodesSetActive(
      state,
      [customSchema.nodes.figcaption, customSchema.nodes.horizontalRule, customSchema.nodes.embed, customSchema.nodes.pdfContainer, customSchema.nodes.note, customSchema.nodes.ghost]
    ) || ["note", "ghost"].includes(state.doc.type.name);

    if(notAllowed) {
      if(dispatch) {
        alert("No es posible insertar un nodo de encabezado en la selección actual. Verifique e intente de nuevo.");
      }

      return false;
    }

    if(!!attrs.id) {
      const { ids } = uniqueIdsKey.getState(state),
        { id } = attrs;
      if(!/^[a-zA-Z][a-zA-Z0-9\-_:.]*$/.test(id)) {
        alert("Identificador '" + id + "' inválido. No se asignó al elemento actual para mantener consistencia.");
        attrs.id = null
      } else if(ids.has(id)) {
        alert("Identificador '" + id + "' utilizado anteriormente. No se asignó al elemento actual para mantener consistencia.");
        attrs.id = null
      }
    }

    setBlockType(customSchema.nodes.heading, attrs)(state, dispatch);

    return true;
  }
}

export function insertNote(type) {
  return (state, dispatch, view) => {
    const { tr, selection } = state,
      { $from, $to } = selection,
      notAllowed = !["autor", "editor"].includes(type) || isNodeActive(state, customSchema.nodes.notesList) || ["note", "ghost"].includes(state.doc.type.name),
      noteNode = customSchema.nodes.note.create({ type });

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar un nodo de nota en la selección. Verifique e intente nuevamente.");
      }

      return false;
    }

    tr.replaceSelectionWith(noteNode)
    if(dispatch) {
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

export function insertGhost() {
  return (state, dispatch, view) => {
    const { tr, selection } = state,
      { $from, $to } = selection,
      notAllowed = isNodesSetActive(state, [customSchema.nodes.note, customSchema.nodes.ghost]) || ["note", "ghost"].includes(state.doc.type.name),
      ghostNode = customSchema.nodes.ghost.create();

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar un nodo de texto fantasma en la selección. Verifique e intente nuevamente.");
      }

      return false;
    }

    tr.replaceSelectionWith(ghostNode)
    if(dispatch) {
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

export function insertSpace(size) {
  return (state, dispatch, view) => {
    const { tr, selection } = state,
      { $from, $to } = selection,
      notAllowed = false; //isNodesSetActive(state, [customSchema.nodes.note]) || ["note"].includes(state.doc.type.name);


    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar un nodo de espacio en la selección. Verifique e intente nuevamente.");
      }

      return false;
    }


    const spaceNode = customSchema.nodes.space.create({ size })
    tr.replaceSelectionWith(spaceNode)

    if(dispatch) {
      dispatch(tr.scrollIntoView());
    }


    return true;

  }
}

export function isPositiveNumber(string) {
  const numeric = Number(string);

  return Number.isFinite(numeric) && numeric > 0;
}

export function wrapInBlockquote(state, dispatch) {
  const { tr, doc } = state,
    notAllowed = isNodesSetActive(state, [customSchema.nodes.figure, customSchema.nodes.figureGraphic, customSchema.nodes.note, customSchema.nodes.ghost])
      || ["note", "ghost"].includes(doc.type.name);
    
  if(notAllowed) {
    if(dispatch) {
      alert("No se puede insertar una cita en la selección actual. Verifique e intente nuevamente.");
    }
    
    return false;
  }
  
  return wrapIn(customSchema.nodes.blockquote)(state, dispatch);
}

export function insertHorizontalRule(attrs = {}) {
  return (state, dispatch, view) => {
    const nodeType = customSchema.nodes.horizontalRule,
      { tr } = state,
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.figure, customSchema.nodes.figureGraphic, customSchema.nodes.embed, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name);

    if(notAllowed) {
      if(dispatch) {
        alert("No se pudo insertar el nodo de regla horizontal en la selección actual. Verifique que la inserción sea permitida.");
      }

      return false;
    }

    if(dispatch) {
      tr.replaceSelectionWith(nodeType.create(attrs));
      dispatch(tr);
    }

    return true;
  }
}

export function insertFigure(src, withCaption) {
  return (state, dispatch) => {
    const { tr } = state, 
      imageNode = image.create({ src }),
      captionNode = figcaption.create(),
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.horizontalRule, customSchema.nodes.embed, customSchema.nodes.pdfContainer, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name);
    let figureNode;

    if(notAllowed || (src !== null && !dispatch) || (!evalImgUrl(src) && !!dispatch)) {
      if(dispatch) {
        alert("No se pudo insertar el nodo de imagen en la selección actual. Verifique que la inserción sea permitida.");
      }

      return false;
    }

    if(withCaption) {
       figureNode = figure.create(null, [imageNode, captionNode]);
    } else {
      figureNode = figureGraphic.create(null, [imageNode]);
    }

    if(dispatch) {
      tr.replaceSelectionWith(figureNode);
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

export function evalImgUrl(url) {
  try {
    const imgUrl = new URL(url),
      { pathname } = imgUrl;

    if(!/^jpe?g|gif|svg|png$/.test(pathname.split(".").at(-1))) {
      return false;
    }

    return true;
  } catch(_) {
    return !!url && url.startsWith("/");
    //return false;
  }
}

export function insertPdf(src, orientation) {
  return (state, dispatch) => {
    const { tr } = state, 
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.horizontalRule, customSchema.nodes.embed, customSchema.nodes.figure, customSchema.nodes.figureGraphic, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name),
      maybeSrc = evalPdfUrl(src);

    if(notAllowed || (src !== null && !dispatch) || (!maybeSrc && !!dispatch)) {
      if(dispatch) {
        alert("No se pudo insertar el nodo de PDF en la selección actual. Verifique que la inserción sea permitida.");
      }

      return false;
    }

    const pdfNode = customSchema.nodes.pdfContainer.create({ src: maybeSrc, orientation: orientation === "vertical" ? "vertical" : "horizontal" });

    if(dispatch) {
      tr.replaceSelectionWith(pdfNode);
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

export function evalPdfUrl(url) {
  try {
    const pdfUrl = new URL(url),
      { pathname } = pdfUrl,
      viewerLocation = "/resources/pdf-viewer/web/viewer.html?file=";

    if(!/^pdf$/.test(pathname.split(".").at(-1))) {
      return false;
    }

    return window.location.origin + viewerLocation + pdfUrl.toString();
  } catch(_) {
    return !!url && url.startsWith("/");
    //return false;
  }
}

export function insertEmbed(src) {
  return (state, dispatch) => {
    const { tr } = state,
      metadata = dispatch ? evalEmbedUrl(src) : {},
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.horizontalRule, customSchema.nodes.figure, customSchema.nodes.figureGraphic, customSchema.nodes.pdfContainer, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name);

    if(notAllowed || (!metadata && dispatch)) {
      if(dispatch) {
        alert("No se pudo insertar el nodo de contenido embebido en la selección actual. Verifique que la inserción sea permitida.");
      }

      return false;
    }

    const { src: link, ...meta } = metadata,
      embedNode = embed.create({ src: link, metadata: meta });

    if(dispatch) {
      tr.replaceSelectionWith(embedNode);
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

function evalEmbedUrl(url) {
  try {
    const embedUrl = new URL(url),
      { host, pathname, searchParams } = embedUrl;
    let provisionalSearchParams = new URLSearchParams(searchParams),
      adjustedUrl = null;

    if(/^(?:(?:www|m|music)\.)?(?:youtube(?:-nocookie)?\.com)$/.test(host)) {
      if(/^\/embed\//.test(pathname)) {
        adjustedUrl = url;
      } else if(/^\/live\//.test(pathname)) {
        adjustedUrl = url.replace("/live/","/embed/");
      } else if(/^\/watch$/.test(pathname)) {
        const videoId = searchParams.get("v"),
          list = searchParams.get("list"),
          baseUrl = "https://www.youtube-nocookie.com/embed";

        if(list !== null) {
          provisionalSearchParams.delete("v");
          provisionalSearchParams.delete("list");
          adjustedUrl = baseUrl + "?list=" + list + "&listType=playlist" +  (provisionalSearchParams.size === 0 ? "" : "&" + provisionalSearchParams.toString());
        } else if(videoId !== null) {
          provisionalSearchParams.delete("v");
          adjustedUrl = baseUrl + "/" + videoId + (provisionalSearchParams.size === 0 ? "" : "?" + provisionalSearchParams.toString());
        }
      }

      if(adjustedUrl === null) {
        return false;
      }

      return {
        src: adjustedUrl,
        "data-type": "video"
      }
    }

    if(host === "youtu.be") {
      const list = provisionalSearchParams.get("list"),
        baseUrl = "https://www.youtube-nocookie.com/embed";
      let adjustedUrl = null;

      if(list) {
        provisionalSearchParameters.delete("v").delete("list");
        adjustedUrl = baseUrl + "?list=" + list + "&listType=playlist" +  (provisionalSearchParams.size === 0 ? "" : "&" + provisionalSearchParams.toString());
      } else {
        provisionalSearchParams.delete("v");
        adjustedUrl = baseUrl + "/" + pathname + (provisionalSearchParams.size === 0 ? "" : "?" + provisionalSearchParams.toString())
      }

      if(adjustedUrl === null) {
        return false;
      }

      return {
        src: adjustedUrl,
        "data-type": "video",
        loading: "lazy"
      }
    }

    if(host === "vimeo.com") {
      return {
        src: "https://player.vimeo.com/video" + pathname,
        "data-type": "video",
        loading: "lazy"
      }
    }

    if(host === "open.spotify.com") {
      let splitPath = pathname.split("/");

      return {
        src: `https://open.spotify.com/embed${splitPath.length === 3 ? pathname : "/" + splitPath.slice(2).join("/")}`,
        "data-type": "spotify",
        loading: "lazy",
        allow: "encrypted-media",
        "data-list": /album|artist|playlist/.test(pathname)
      }
    }

    return false;
  } catch(_) {
    return false;
  }
}

export function insertTable(rows, cols) {
  return (state, dispatch) => {
    const { tr } = state,
      cell = table_cell.createAndFill(),
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.figcaption, customSchema.nodes.table, customSchema.nodes.horizontalRule, customSchema.nodes.embed, customSchema.nodes.pfdContainer, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name);

    if(!cell || notAllowed || Number.isNaN(rows) || Number.isNaN(cols) || rows < 1 || cols < 1) {
      if(dispatch) {
        alert("No se pudo insertar el nodo en la selección actual. Verifique que la inserción sea permitida.");
      }

      return false;
    }

    const createRow = () => { return table_row.create(null, Fragment.fromArray(Array(cols).fill(cell))); },
      tableNode = table.create(null, Fragment.fromArray(Array(rows).fill(createRow())));

    if(dispatch) {
      const offset = tr.selection.anchor + 1;
      dispatch(tr.replaceSelectionWith(tableNode).scrollIntoView()/*.setSelection(TextSelection.near(tr.doc.resolve(offset)))*/);
    }

    return true;
  }
}

export function insertNotesList(type) {
  return (state, dispatch) => {
    const { tr } = state,
      listItemNode = customSchema.nodes.listItem.createAndFill(),
      notesListNode = customSchema.nodes.notesList.create({ type }, [listItemNode]),
      notAllowed = isNodesSetActive(state, [customSchema.nodes.heading, customSchema.nodes.figcaption, customSchema.nodes.embed, customSchema.nodes.pdfContainer, customSchema.nodes.horizontalRule, customSchema.nodes.heading, customSchema.nodes.table, customSchema.nodes.blockquote, customSchema.nodes.orderedList, customSchema.nodes.unorderedList, customSchema.nodes.note, customSchema.nodes.ghost])
        || ["note", "ghost"].includes(state.doc.type.name);
    let isPresent = false;

    if(notAllowed) {
      if(dispatch) {
        alert("No se puede insertar un nodo de notas en la selección. Verifique e intente de nuevo.");
      }

      return false;
    }

    tr.doc.nodesBetween(0, tr.doc.content.size, (node, pos) => {
      if(node.type.name === "notesList" && node.attrs.type === type) {
        isPresent = true;

        return false;
      }
    });

    if(isPresent) {
      if(dispatch) {
        alert("No se pudo insertar un nodo adicional de lista de notas de " + type + "porque ya está presente. Verifique e intente de nuevo.");
      }

      return false;
    }

    if(dispatch) {
      dispatch(tr.replaceSelectionWith(notesListNode).scrollIntoView());
    }

    return true;
  }
}

export function insertDetailsNodeview(name, open = false) {
  return (state, dispatch) => {
    const { tr } = state,
      detailsContentNode = detailsContent.createAndFill(),
      paragraphSummaryNode = paragraphSummary.create(),
      summaryNode = summary.create(null, [paragraphSummaryNode]),
      detailsNode = details.create({ name, open }, [summaryNode, detailsContentNode]),
      notAllowed = !!getNode(state, customSchema.nodes.figure) || !!getNode(state, customSchema.nodes.summary);

    if(!detailsContentNode || !summaryNode || !detailsNode || notAllowed) {
      return false;
    }

    if(dispatch) {
      dispatch(tr.replaceSelectionWith(detailsNode).scrollIntoView());
    }

    return true;
  }
}

export function insertDetails(name) {
  return (state, dispatch) => {
    const { tr } = state,
      detailsContentNode = detailsContent.createAndFill(),
      summaryNode = summary.createAndFill(),
      detailsNode = details.create({ name, open: false }, [summaryNode, detailsContentNode]),
      notAllowed = !!getNode(state, customSchema.nodes.figure) || !!getNode(state, customSchema.nodes.summary);

    if(!detailsContentNode || !summaryNode || !detailsNode || notAllowed) {
      return false;
    }

    if(dispatch) {
      dispatch(tr.replaceSelectionWith(detailsNode).scrollIntoView());
    }

    return true;
  }
}

// Plugin view functions.

export function setActiveTool(tool) {
  return (state, dispatch) => {
    const { tr } = state;

    if(dispatch) {
      dispatch(tr.setMeta(menuPluginKey, { action: "toolChange", tool }));
    }

    return true;
  }
}

// Helper functions.

function newlineInCode(state, dispatch) {
  const { $cursor } = state.selection;
  if (!!$cursor && !!$cursor.parent.type.spec.code) {
    if(dispatch) {
      dispatch(state.tr.insertText("\n"));
    }

    return true;
  }

  return false;
};

function getMarkRange(doc, from, to, markType) {
  let range = { from, to };
  doc.nodesBetween(from, to, (node, pos) => {
    if(node.marks.some(mark => mark.type.name === markType.name)) {
      range.from = Math.min(range.from, pos);
      range.to = Math.max(range.to, pos + node.nodeSize);
    }
  });

  return range;
};

export function deleteFigure(state, dispatch, view) {
  const { selection, tr, doc } = state,
    { $cursor, empty, node } = selection;

  if(node && node.type.name === "figure") {
    if(dispatch) {
      dispatch(tr.deleteSelection());
    }

    return true;
  }

  if(empty && $cursor) {
    const parent = $cursor.parent;

    if(parent.type.name === "figcaption" && $cursor.parentOffset === 0) {
      const grandParent = $cursor.node(-1),
        grandParentPos = $cursor.before(-1);
      if(grandParent.type.name === "figure") {
        if(dispatch) {
          dispatch(tr.delete(grandParentPos, grandParentPos + grandParent.nodeSize).scrollIntoView());
        }

        return true;
      }
    }
  }

  return false;
}

export function deletePdf(state, dispatch, view) {
  const { selection, tr, doc } = state,
    { $cursor, empty, node } = selection;

  if(node && node.type.name === "pdfContainer") {
    if(dispatch) {
      dispatch(tr.deleteSelection());
    }

    return true;
  }

  if(empty && $cursor) {
    const parent = $cursor.parent;

    if(parent.type.name === "pdfContainer") {
      if(dispatch) {
        dispatch(tr.delete(grandParentPos, grandParentPos + grandParent.nodeSize).scrollIntoView());
      }

      return true;
    }
  }

  return false;
}

function customBackspaceCommand(state, dispatch, view) {
  const { $cursor, empty } = state.selection,
    { doc } = state;

  if(empty) {
    return false;
  }

  if(!!$cursor && $cursor.parentOffset === 0) {
    const beforeNode = $cursor.before(),
      nodeBefore = beforeNode && doc.nodeAt(beforeNode - 1);

    if($cursor.parent.textContent === "" && lift(state, dispatch)) {
      return true;
    }

    if(nodeBefore && nodeBefore.type.name === "figure" && nodeBefore.childCount === 0) {
      if(dispatch) {
        const tr = state.tr.delete(beforeNode - 1, beforeNode + nodeBefore.nodeSize - 1);
        dispatch(tr);
      }

      return true;
    }
  }

  return false;
}
